import { useState, type ReactNode } from "react";
import { ChevronDown, CornerDownRight, HelpCircle, Loader2, MessageCircleQuestion, SquareKanban, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Hint } from "@/components/ui/tooltip";
import { useLongPress } from "@/hooks/use-long-press";
import { waitingLabel } from "@/lib/format";
import type { AgentOpenQuestion } from "@/lib/types";
import { cn } from "@/lib/utils";
import { isStaleQuestion, questionTitle } from "@/lib/waiting";

/**
 * The one look a question waiting on the user has, wherever it shows: the
 * `--waiting` tint cards carry, its title, how long it has waited, an "old" mark
 * past a day, and the card it is about. The chat's pinned bar, the agent list's
 * pill, the Awaiting page and a card's sheet all build from these pieces.
 */

/** "waiting 3h", then "old" once it has waited a day — probably moot, never closed quietly. */
export function QuestionAge({
  since,
  now,
  className,
  markOnly = false,
}: {
  since: string;
  now: number;
  className?: string;
  /** Just the "old" mark, where the time is already said beside it. */
  markOnly?: boolean;
}) {
  const stale = isStaleQuestion(since, now);
  if (markOnly && !stale) return null;
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1 text-[11px] font-medium tabular-nums text-waiting", className)}>
      {markOnly ? null : waitingLabel(since, now)}
      {stale ? (
        <Hint label="Asked over a day ago. If it no longer matters, mark it No longer needed.">
          <span className="rounded-full border border-waiting/40 px-1.5 text-[9.5px] font-semibold uppercase tracking-wide">old</span>
        </Hint>
      ) : null}
    </span>
  );
}

/** The card a question is about, as a chip that opens it. A span, since it often sits inside a row that is a button. */
export function QuestionCardChip({ title, onOpen }: { title: string | null; onOpen: () => void }) {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        event.stopPropagation();
        onOpen();
      }}
      className="inline-flex max-w-56 items-center gap-1 rounded-full border border-border/80 bg-card px-1.5 py-px text-[10.5px] text-muted-foreground hover:border-ring/50 hover:text-foreground"
    >
      <SquareKanban className="size-3 shrink-0" />
      <span className="truncate">{title ?? "the card"}</span>
    </span>
  );
}

/**
 * Pinned under an agent's chat header while any question is open, however far up
 * the transcript it was asked. The line jumps to the (oldest) question; the chevron
 * opens every one with its answers, so it can be answered without scrolling at all.
 */
export function PendingQuestionsBar({
  questions,
  now,
  canRespond,
  onJump,
  onAnswer,
  onReply,
  onDismiss,
  onOpenTask,
}: {
  questions: AgentOpenQuestion[];
  now: number;
  canRespond: boolean;
  onJump: (messageId: string) => void;
  /** One of the offered options, sent as the answer. */
  onAnswer: (messageId: string, option: string) => Promise<void>;
  /** Write an answer in the composer. */
  onReply: (messageId: string) => void;
  /** "No longer needed". */
  onDismiss: (messageId: string) => Promise<void>;
  onOpenTask: (taskId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const first = questions[0];
  if (!first) return null;
  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };
  const many = questions.length > 1;
  return (
    <div className="border-b border-waiting/40 bg-waiting/[0.07]" role="region" aria-label="Questions waiting on you">
      <div className="mx-auto flex max-w-3xl items-center gap-2 px-3 py-1.5 win-md:px-6">
        <button
          type="button"
          onClick={() => onJump(first.messageId)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left text-[12.5px]"
          aria-label={`Show the question: ${questionTitle(first.body)}`}
        >
          <MessageCircleQuestion className="size-3.5 shrink-0 text-waiting" />
          <span className="shrink-0 font-semibold text-waiting">
            {questions.length} question{many ? "s" : ""} waiting
          </span>
          <span className="shrink-0 text-muted-foreground">·</span>
          <span className="min-w-0 flex-1 truncate">{questionTitle(first.body)}</span>
          <QuestionAge since={first.createdAt} now={now} className="hidden win-sm:inline-flex" />
        </button>
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={open ? "Hide the questions" : "Answer from here"}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <ChevronDown className={cn("transition-transform", open && "rotate-180")} />
        </Button>
      </div>
      {open ? (
        <ul className="mx-auto max-h-[45dvh] max-w-3xl space-y-1.5 overflow-y-auto px-3 pb-2.5 scrollbar-slim win-md:px-6">
          {questions.map((question) => (
            <li key={question.messageId} className="rounded-md border border-waiting/35 bg-card/80 px-3 py-2">
              {/* One question: the bar's own line already names it. */}
              <div className={cn("flex items-start gap-2", !many && "hidden")}>
                <HelpCircle className="mt-0.5 size-3.5 shrink-0 text-waiting" />
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left text-[13px] leading-snug hover:underline"
                  onClick={() => onJump(question.messageId)}
                >
                  <span className="line-clamp-2">{questionTitle(question.body)}</span>
                </button>
                <QuestionAge since={question.createdAt} now={now} />
              </div>
              <div className={cn("flex flex-wrap items-center gap-1.5", many && "mt-1.5 pl-5")}>
                {question.taskId ? <QuestionCardChip title={question.taskTitle} onOpen={() => onOpenTask(question.taskId!)} /> : null}
                {canRespond ? (
                  <>
                    {question.options.map((option) => (
                      <Button
                        key={option}
                        size="xs"
                        variant="outline"
                        disabled={busy !== null}
                        onClick={() => void run(`${question.messageId}:${option}`, () => onAnswer(question.messageId, option))}
                      >
                        {busy === `${question.messageId}:${option}` ? <Loader2 className="animate-spin" /> : null}
                        {option}
                      </Button>
                    ))}
                    <Button size="xs" variant="outline" disabled={busy !== null} onClick={() => onReply(question.messageId)}>
                      <CornerDownRight /> Answer
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={busy !== null}
                      onClick={() => void run(`${question.messageId}:dismiss`, () => onDismiss(question.messageId))}
                    >
                      {busy === `${question.messageId}:dismiss` ? <Loader2 className="animate-spin" /> : <X />} No longer needed
                    </Button>
                  </>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * The agent list's "needs you", naming what it needs: hovering shows the question,
 * a long press on a touch screen lists them, and a tap opens the chat at the oldest.
 * A span rather than a button, because the row it sits in is one.
 */
export function NeedsYouPill({
  questions,
  now,
  onOpen,
}: {
  questions: AgentOpenQuestion[];
  now: number;
  /** Opens the chat at this question; null when there is nothing to point at (non-admins). */
  onOpen: (messageId: string | null) => void;
}) {
  const [menu, setMenu] = useState(false);
  const hold = useLongPress({ onLongPress: () => setMenu(true), disabled: questions.length === 0 });
  const first = questions[0];
  const label = first
    ? questions.length === 1
      ? `Waiting on you: ${questionTitle(first.body)}`
      : `${questions.length} questions waiting on you. The oldest: ${questionTitle(first.body)}`
    : "Waiting on you";
  const pill: ReactNode = (
    <span
      role="button"
      tabIndex={0}
      aria-label={label}
      data-own-hold
      className="inline-flex shrink-0 cursor-pointer select-none items-center gap-1 rounded-full bg-waiting/15 px-1.5 text-[10px] font-semibold text-waiting [-webkit-touch-callout:none]"
      {...hold}
      onPointerDown={(event) => {
        hold.onPointerDown(event);
        // Radix's trigger opens on pointerdown; this one opens on a long press only.
        event.preventDefault();
        // The row it sits in has a hold of its own: this press is the pill's.
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(first?.messageId ?? null);
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        event.stopPropagation();
        onOpen(first?.messageId ?? null);
      }}
    >
      {questions.length > 1 ? <span className="tabular-nums">{questions.length}</span> : null}
      needs you
    </span>
  );
  return (
    <DropdownMenu open={menu} onOpenChange={setMenu}>
      <Hint label={label}>
        <DropdownMenuTrigger asChild>{pill}</DropdownMenuTrigger>
      </Hint>
      <DropdownMenuContent align="end" className="w-72" onClick={(event) => event.stopPropagation()}>
        <DropdownMenuLabel>Waiting on you</DropdownMenuLabel>
        {questions.map((question) => (
          <DropdownMenuItem key={question.messageId} onSelect={() => onOpen(question.messageId)} className="items-start">
            <HelpCircle className="mt-0.5 text-waiting" />
            <span className="min-w-0 flex-1">
              <span className="line-clamp-2 text-[12.5px]">{questionTitle(question.body)}</span>
              <QuestionAge since={question.createdAt} now={now} className="mt-0.5" />
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
